import {
  QBackLink,
  QBodyLayout,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QHeader,
  QHorizontalMetadata,
  QNotFound,
  QOpenPropertiesPanelButton,
  QPageLoader,
  QPropertiesPanel,
  QStack,
  QText,
  QTitle,
  QTogglePropertiesPanelButton,
} from "@qualio/ui-components";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import { useDeleteElementDrawer } from "../../hooks/useDesignElementDrawer";
import {
  isDesignElementISORisk,
  RequirementDesignElement,
  TestCaseDesignElement,
} from "@design-controls/types";
import { isSoureEditableAndDeletable } from "../../domain/editableSource";
import { useNavigate } from "react-router";
import { DesignReviewOverviewTable } from "../../components/DesignReviewOverviewTable/DesignReviewOverviewTable";
import { TestCaseTable } from "../../components/OverviewTable/TestCaseTable";
import { RequirementTable } from "../../components/OverviewTable/RequirementTable";
import { ISORiskLevel } from "./components/ISORiskLevel";
import { GapAnalysis } from "../../components/GapAnalysis/GapAnalysis";
import { sourceDisplayName } from "../../constants/sourceDisplayName";
import { DesignElementStatus } from "../../components/DesignElementStatus/DesignElementStatus";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";

export const ISORiskOverview: React.FC = () => {
  const { t } = useTranslation();
  const { name } = useCurrentProduct();
  const { isLoading, designElement, designElementHistory, version } =
    useDeleteElementDrawer();
  const navigate = useNavigate();
  const permissions = useHasPermission([UserPermissions.EDIT_DESIGN_ELEMENTS]);

  const onEditClick = useCallback(() => {
    navigate("edit");
  }, [navigate]);

  const sourceRequirements = useMemo(() => {
    if (!designElement || !isDesignElementISORisk(designElement)) {
      return [];
    }

    return [...(designElement.sourceItems ?? [])].filter((element) =>
      element.type.startsWith("req"),
    ) as RequirementDesignElement[];
  }, [designElement]);

  const sourceTestCases = useMemo(() => {
    if (!designElement || !isDesignElementISORisk(designElement)) {
      return [];
    }

    return [...(designElement.sourceItems ?? [])].filter((element) =>
      element.type.startsWith("testCase"),
    ) as TestCaseDesignElement[];
  }, [designElement]);

  const mitigatesRequirements = useMemo(() => {
    if (!designElement || !isDesignElementISORisk(designElement)) {
      return [];
    }

    return [...(designElement.mitigationItems ?? [])].filter((element) =>
      element.type.startsWith("req"),
    ) as RequirementDesignElement[];
  }, [designElement]);

  const mitigatesTestCases = useMemo(() => {
    if (!designElement || !isDesignElementISORisk(designElement)) {
      return [];
    }

    return [...(designElement.mitigationItems ?? [])].filter((element) =>
      element.type.startsWith("testCase"),
    ) as TestCaseDesignElement[];
  }, [designElement]);

  if (isLoading) {
    return <QPageLoader />;
  }

  if (!designElement || !isDesignElementISORisk(designElement)) {
    return <QNotFound />;
  }

  return (
    <QBodyLayout.Default>
      <QHeader>
        <QBackLink href="../risk/iso" data-cy="back-btn">
          {t("products.backLink", { productName: name })}
        </QBackLink>
        <QTitle>
          <QText as="span" color="gray.500">
            {designElement?.code}
          </QText>{" "}
          {designElement?.title}
        </QTitle>
        <QHorizontalMetadata>
          <DesignElementStatus {...designElement} />
          <QDateMetadataItem
            label={t("common.metadata.created")}
            value={new Date(designElement?.created ?? 0)}
          />
          <QDateMetadataItem
            label={t("common.metadata.lastModified")}
            value={new Date(designElement?.updated ?? 0)}
          />
        </QHorizontalMetadata>
        <QButtonGroup>
          {isSoureEditableAndDeletable(designElement.source) &&
            permissions[UserPermissions.EDIT_DESIGN_ELEMENTS] &&
            version === "v0" && (
              <QButton
                leftIcon="Edit3"
                variant="outline"
                data-cy="edit"
                onClick={onEditClick}
              >
                {t("common.actions.edit")}
              </QButton>
            )}
          {!isSoureEditableAndDeletable(designElement.source) && (
            <a
              href={designElement.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <QButton
                data-cy="integration-button"
                variant="outline"
                rightIcon="ExternalLink"
              >
                {sourceDisplayName(designElement.source)}
              </QButton>
            </a>
          )}
          <QOpenPropertiesPanelButton />
        </QButtonGroup>
        <GapAnalysis
          policyIssues={designElement.policyIssues}
          type={designElement.type}
        />
      </QHeader>
      <QStack gap="16px" data-cy="iso-risk-overview">
        <QText fontSize="xl" fontWeight={600}>
          {t("risks.iso.overview.initialAssessment")}
        </QText>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.iso.overview.fields.hazard")}
          </QText>
          <QText fontSize="sm" data-cy="hazard">
            {designElement.hazard}
          </QText>
        </QStack>

        <QStack direction="row" columnGap="32px">
          <QStack>
            <QText fontSize="sm" fontWeight={600}>
              {t("risks.iso.overview.fields.riskLevel")}
            </QText>
            <QText fontSize="sm">
              <ISORiskLevel
                level={designElement.preMitigation?.riskLevel}
                data-cy="pre-mitigation-level"
              />
            </QText>
          </QStack>
          <QStack>
            <QText fontSize="sm" fontWeight={600}>
              {t("risks.iso.overview.fields.probability")}
            </QText>
            <QText fontSize="sm" data-cy="pre-mitigation-probability">
              {designElement.preMitigation?.probability}
            </QText>
          </QStack>
          <QStack>
            <QText fontSize="sm" fontWeight={600}>
              {t("risks.iso.overview.fields.severity")}
            </QText>
            <QText fontSize="sm" data-cy="pre-mitigation-severity">
              {designElement.preMitigation?.severity}
            </QText>
          </QStack>
        </QStack>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.iso.overview.fields.reasonableForeseeableUseMisuse")}
          </QText>
          <QText fontSize="sm" data-cy="foreseeableUseMisuse">
            {designElement.foreseeableUseMisuse}
          </QText>
        </QStack>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.iso.overview.fields.hazardSituation")}
          </QText>
          <QText fontSize="sm" data-cy="hazardSituation">
            {designElement.hazardSituation}
          </QText>
        </QStack>

        <QStack>
          <QText fontSize="sm" fontWeight={600}>
            {t("risks.iso.overview.fields.harm")}
          </QText>
          <QText fontSize="sm" data-cy="harm">
            {designElement.harm}
          </QText>
        </QStack>

        {designElementHistory && designElementHistory?.length > 0 && (
          <DesignReviewOverviewTable changeHistory={designElementHistory} />
        )}

        <RequirementTable requirements={sourceRequirements} />
        <TestCaseTable testCases={sourceTestCases} />

        {designElement.riskControl && (
          <>
            <QText fontSize="xl" fontWeight={600} data-cy="risk-control-header">
              {t("risks.iso.overview.riskControl")}
            </QText>

            <QStack>
              <QText fontSize="sm" fontWeight={600}>
                {t("risks.iso.overview.fields.mitigation")}
              </QText>
              <QText fontSize="sm" data-cy="mitigation">
                {designElement.riskControl.mitigation}
              </QText>
            </QStack>

            <QStack direction="row" columnGap="32px">
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.iso.overview.fields.riskLevel")}
                </QText>
                <QText fontSize="sm">
                  {designElement.postMitigation?.riskLevel && (
                    <ISORiskLevel
                      level={designElement.postMitigation?.riskLevel}
                      data-cy="post-mitigation-level"
                    />
                  )}
                </QText>
              </QStack>
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.iso.overview.fields.probability")}
                </QText>
                <QText fontSize="sm" data-cy="post-mitigation-probability">
                  {designElement.postMitigation?.probability}
                </QText>
              </QStack>
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.iso.overview.fields.severity")}
                </QText>
                <QText fontSize="sm" data-cy="post-mitigation-severity">
                  {designElement.postMitigation?.severity}
                </QText>
              </QStack>
            </QStack>

            <RequirementTable requirements={mitigatesRequirements} />
            <TestCaseTable testCases={mitigatesTestCases} />
          </>
        )}

        <QPropertiesPanel openOnMount>
          <QStack spacing="8px" w="300px">
            <QStack direction="row" justifyContent="space-between">
              <QTitle>{t("risks.iso.overview.properties.riskProfile")}</QTitle>
              <QTogglePropertiesPanelButton />
            </QStack>
            <QStack>
              <QText fontSize="sm" fontWeight={600}>
                {t("risks.iso.overview.properties.riskProfile")}
              </QText>
              <QText fontSize="sm" data-cy="risk-profile">
                {t("risks.iso.overview.properties.riskProfileValue")}
              </QText>
            </QStack>
            {designElement.category && (
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.iso.overview.properties.category")}
                </QText>
                <QText fontSize="sm" data-cy="category">
                  {designElement.category?.label}
                </QText>
              </QStack>
            )}
          </QStack>
        </QPropertiesPanel>
      </QStack>
    </QBodyLayout.Default>
  );
};
